import { Link } from 'react-router-dom';
import { Translate, ArrowRight, ClipboardText } from '@phosphor-icons/react';
import { ServiceDetailLayout } from '../../components/ServiceDetailLayout';
import { SERVICES_INDEX, SERVICE_HERO_IMAGES } from '../../lib/servicesIndex';

const OnlineIntakeCallout = () => (
  <section className="py-20" style={{ background: 'linear-gradient(135deg, #4A0000 0%, #800000 100%)' }}>
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
      <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#D4AF37]/20 mb-5">
        <ClipboardText weight="duotone" size={36} className="text-[#D4AF37]" />
      </div>
      <p className="text-[#D4AF37] text-xs font-semibold tracking-[0.3em] uppercase mb-3">Skip The Phone Tag</p>
      <h2 className="font-serif-display text-3xl sm:text-4xl font-semibold mb-4 leading-tight">
        Start your apostille order online — <span className="italic">in under 5 minutes.</span>
      </h2>
      <p className="text-white/85 text-base leading-[1.85] max-w-2xl mx-auto mb-7">
        Use our secure online intake form to submit your documents and details. You'll receive a private tracking link the moment you submit — bookmark it to follow every milestone, from received to apostilled to shipped.
      </p>
      <Link
        to="/services/apostille/order"
        data-testid="apostille-order-cta"
        className="inline-flex items-center gap-2 px-7 py-3.5 rounded-md bg-[#D4AF37] hover:bg-[#B8941F] text-[#1A1A1A] text-sm font-semibold tracking-[0.15em] uppercase transition-colors"
      >
        Begin Online Order
        <ArrowRight weight="bold" size={14} />
      </Link>
    </div>
  </section>
);

const TranslationCallout = () => (
  <section className="py-20 bg-white border-t border-[#E8E2D5]">
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-2 flex lg:justify-center">
          <div className="w-20 h-20 rounded-full bg-[#D4AF37]/15 flex items-center justify-center">
            <Translate weight="duotone" size={42} className="text-[#D4AF37]" />
          </div>
        </div>
        <div className="lg:col-span-10">
          <p className="text-[#D4AF37] text-xs font-semibold tracking-[0.3em] uppercase mb-3">
            Pairs With Apostille
          </p>
          <h2 className="font-serif-display text-3xl sm:text-4xl font-semibold text-[#800000] mb-5 leading-tight">
            Need it in English first? <span className="italic">Translation, handled.</span>
          </h2>
          <p className="text-[#3C3C3C] text-base leading-[1.85] mb-5 max-w-3xl">
            Many apostille projects start with a foreign-language original — a Spanish birth certificate, a Portuguese marriage record, a French diploma. India coordinates a certified translation, notarizes the translator's certification of accuracy, and walks the bilingual package through the Tennessee Secretary of State as one continuous project. One contact, one project plan, one delivery.
          </p>
          <p className="text-[#3C3C3C] text-base leading-[1.85] mb-7 max-w-3xl">
            And the reverse direction works too: English documents translated and certified for use abroad, with apostille added when the destination country requires it.
          </p>
          <Link
            to="/services/document-translation"
            data-testid="apostille-translation-link"
            className="inline-flex items-center gap-2 text-sm font-semibold tracking-[0.15em] uppercase text-[#800000] hover:text-[#D4AF37] transition-colors"
          >
            Learn more about Document Translation
            <ArrowRight weight="bold" size={14} />
          </Link>
        </div>
      </div>
    </div>
  </section>
);

export const ApostillePage = () => (
  <ServiceDetailLayout
    eyebrow="Apostille & International Certification"
    title="Apostille &"
    titleAccent="Authentications."
    subtitle="Expedited Tennessee Secretary of State apostille for births, marriages, diplomas, FBI background checks, adoption files, and corporate documents — with professional document translation coordination available alongside the state-level authentication pipeline. Embassy-ready, overnight when you need it."
    heroImage={SERVICE_HERO_IMAGES.apostille}
    intro={{
      kicker: 'Tennessee SOS Specialist · Translation-Inclusive',
      heading: 'Embassy-ready paperwork — without the wait.',
      body: [
        'Apostille is the international certification stamp that lets a Tennessee-issued document be recognized abroad. India works directly with the Tennessee Secretary of State filing window and knows exactly what each consulate, embassy, and foreign ministry expects to see — so your file gets approved the first time.',
        "Alongside the standard state-level authentication pipeline, India also offers professional document translation coordination as part of the same project. Foreign-language originals are routed to certified translators, the translator's certification of accuracy is notarized, and the entire bilingual package moves through the Tennessee Secretary of State for apostille — all under one project plan, one point of contact, and one delivery.",
        "Whether you're relocating overseas, finalizing an international adoption, applying for dual citizenship, or filing corporate documents in another country, India handles every step from translation (if needed) to notarization, state authentication, and courier pickup.",
      ],
      pullQuote: {
        text: "India is the only Nashville notary I'll trust with apostille — fast, accurate, and never a rejected filing.",
        attribution: 'Recurring Client · International Adoption',
      },
    }}
    bullets={[
      { title: 'Birth, Marriage & Death Certificates', description: 'Vital records issued by Tennessee Department of Health, fully apostilled.' },
      { title: 'FBI & TBI Background Checks', description: 'Federal background check authentication for visa, work permit, and immigration filings.' },
      { title: 'Diplomas & Transcripts', description: 'University degrees, school records, and academic transcripts certified for foreign study or employment.' },
      { title: 'Power of Attorney & Affidavits', description: 'Notarized first by India, then walked to the Secretary of State for same-week turnaround.' },
      { title: 'Corporate & Business Filings', description: 'Articles of incorporation, certificates of good standing, and contracts for international expansion.' },
      { title: 'Certified Document Translation', description: 'Foreign-language originals translated, certified, and apostilled as one continuous project.' },
      { title: 'Same-Week & Expedited Service', description: 'Standard 5–7 business days. Need it faster? Ask about same-day or next-day expedited filing.' },
    ]}
    extraSection={<><OnlineIntakeCallout /><TranslationCallout /></>}
    faqs={[
      { q: 'How long does Tennessee apostille take?', a: 'Typical turnaround is 5–7 business days end-to-end. With expedited service, India can usually return a fully apostilled document in 24–48 hours.' },
      { q: 'Do you handle the FBI background check authentication?', a: "Yes — federal documents require an additional authentication through the U.S. Department of State. India coordinates the full chain so you don't have to mail anything yourself." },
      { q: 'Which countries accept Tennessee apostille?', a: "Any country that's a party to the 1961 Hague Apostille Convention (most countries worldwide). For non-Hague countries, India can guide you through embassy legalization instead." },
      { q: 'Can you ship the finished document internationally?', a: 'Absolutely — India can return your apostilled documents via FedEx, UPS, or DHL anywhere in the world.' },
      { q: 'What if my document is in another language?', a: 'India coordinates certified translation, notarizes the translator certification, and apostilles the bilingual package as a single project. See the Document Translation page for the full process.' },
    ]}
    relatedSlugs={['document-translation', 'remote-online-notarization', 'general-notary']}
    servicesIndex={SERVICES_INDEX}
  />
);
